// S10 — OP RUN (spec 04 §4 floor row 10, slice 04-B-3).
//
// Giant Start/Pause/Resume/Complete, the qty stepper + scrap-reason grid
// (module 05's causes, localized), material scan issue. EVERY action enqueues
// a `production_event` through the ONE outbox (D-048/D-054) with
// `occurred_at` stamped AT TAP — the builders in lib/production-floor.ts take
// the tap clock as an argument precisely so this screen cannot re-stamp on
// retry — and the screen reflects the outbox honestly: per-op pending count,
// the D-080 needs_review outcome, and the not-saved state.
//
// 04-C-5 ADDS THE DEFECT FLAG and the two STATE MARKERS §5 r15 puts on every
// queue operation: `qc_hold` (§2.2 — the step is on quality hold and §3.2 will
// refuse Start/Resume/Complete/report until e32 clears it) and
// `rework_reopened_at` (§3.2's rework re-open — this card is a RE-DO, and while
// it is set the operator's reports book as `is_rework` and their labor as
// `entry_kind = 'rework'`). Both are rendered BEFORE the taps, for the same
// reason every other guard hint is: a refusal the operator could have been told
// about beforehand is a walk to a machine for nothing.
//
// Downtime arrived with 04-C-1,
// CALIBRATION with 04-C-2 (the §3.3 step-7 form: "Machine says" / "Measured" +
// barrel label + method — measured is the only number costing/inventory ever
// use, D-025), and SERIAL CAPTURE with 04-C-3 (the §3.3 step-8 form). Where a
// still-absent control will make the server refuse, the hint is shown BEFORE
// the tap — a local warning, never a client-side block that second-guesses the
// server (§3.2's guard is the authority; the tap still goes through and lands
// in review if refused).
//
// THE SCAN FIELDS ARE `ScanInput`, NOT TEXT BOXES, and that is the difference
// between a screen an operator uses and one they route around: the ring
// scanners KazKabel already owns type their payload as fast as the OS delivers
// keystrokes and end with Enter, and `ScanInput` claims exactly that burst, re-
// arms itself for the next bobbin without a tap, and offers the camera only
// when one is genuinely available. Typing a 20-character serial with gloves on
// is the fallback, never the demand.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  FloorButton,
  FloorEmpty,
  FloorNeedsConnection,
  FloorNotSaved,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { QtyStepper } from "../components/qty-stepper";
import { ScanInput } from "../components/scan-input";
import { floorPhotoStore, useFloorOutbox, useFloorSubmit } from "../hooks/use-floor-outbox";
import { capturePhotos, floorPhotoProblem, MAX_FLAG_PHOTOS } from "../lib/photo-upload";
import { normalizeQty } from "../lib/qty-entry";
import { formatQuantity, unitSymbol } from "../../../lib/quantity";
import { useStationSnapshot, useStationSnapshotRefresh } from "../hooks/use-station";
import { floorFeedback } from "../lib/floor-feedback";
import {
  asFloorOp,
  calibrationRecordedOp,
  defectFlagProblem,
  defectHandoffFlaggedOp,
  downtimeEndedOp,
  downtimeStartedOp,
  effectiveOpStatus,
  localizedName,
  materialManualOp,
  materialManualReturnOp,
  materialScanOp,
  materialScanReturnOp,
  serialCaptureProblem,
  serialLotCapturedOp,
  opCompletedOp,
  opPausedOp,
  opResumedOp,
  opStartedOp,
  pendingEventsForOp,
  pendingOpenDowntimeForOp,
  pinDevice,
  pinnedDevice,
  qtyReportedOp,
  qtyReportProblem,
  resolveDevicePin,
  tapOutcomeOf,
  type CalibrationMethod,
  type DevicePin,
  type FloorDowntimeReason,
  type FloorOpenDowntime,
  type ProductionEventOp,
  type SerialLotDirection,
  type StationMaterialExpectation,
  type TapContext,
  type TapOutcome,
} from "../lib/production-floor";
import { DeviceIdentity } from "../components/device-identity";
import { itemLabel } from "../lib/floor-outbox";
import { snapshotAgeParts } from "../lib/floor-read-cache";
import "./floor-production.css";

/**
 * A quantity the way the station card writes it: the reader's separators and
 * no padding zeros ("0 of 6,000 m", not the wire's "0.000 of 6000.000"). A
 * value the formatter cannot read is shown as it came rather than blanked.
 */
function floorQty(value: string, locale: string, unit?: string): string {
  const text = formatQuantity(value, { locale, precision: 3, unit });
  return text.length > 0 ? text : value;
}

/**
 * WHETHER THIS SCREEN MAY TAP, AND — WHEN IT MAY NOT — WHY, IN WORDS (D-118).
 *
 * Every panel on this screen was gated by the same three-clause boolean, spelt
 * out seven times, and the only account of a `false` was a hint at the top of a
 * 2071 px document. One value now carries both halves, so a panel cannot be
 * disabled without carrying the sentence that explains it: the type makes the
 * silent case unspellable.
 *
 * THE INPUTS DELIBERATELY STAY LIVE, and this is the one part of the review
 * finding that is answered with a reason rather than with a `disabled`. The
 * complaint was a form that PRESENTS AS USABLE AND SILENTLY REFUSES; the cure
 * for that is the refusal being legible at the control the operator is aiming
 * at, which `disabledReason` now does on every submit, plus the fix being one
 * tap away on this same screen (the picker below). Deadening the numpad on top
 * of that would buy nothing and cost the ordinary sequence: key 420, notice the
 * picker, tap the tablet, send. With the fields dead, that operator retypes the
 * quantity — which is the same lost work the finding is about, arriving from
 * the other direction. A field that accepts what you type and a button that
 * says why it cannot send it yet is an honest form; a screen that refuses your
 * keystrokes without a word is not more honest, only emptier.
 */
interface TapGate {
  readonly blocked: boolean;
  /** Undefined only when nothing is blocking — never "blocked, no reason". */
  readonly reason: string | undefined;
}

export function OpRunScreen() {
  const { t, i18n } = useTranslation();
  const { workCenterId, opId } = useParams({ from: "/floor/station/$workCenterId/op/$opId" });
  const read = useStationSnapshot(workCenterId);
  const refresh = useStationSnapshotRefresh(workCenterId);
  const outbox = useFloorOutbox();
  const { state, submit, reset } = useFloorSubmit();
  const [outcome, setOutcome] = useState<TapOutcome | undefined>(undefined);
  // Set when the evidence blobs could not be persisted BEFORE the flag was
  // queued (D-096) — the same "redo the work" outcome as a failed enqueue, and
  // rendered through the same FloorNotSaved banner.
  const [photoNotSaved, setPhotoNotSaved] = useState(false);
  // The scan string the LAST tap carried, when it carried one. It is what lets
  // an `unknown_reference` refusal name the code that failed instead of
  // showing the operator a server enum they cannot act on.
  const [outcomeScan, setOutcomeScan] = useState<string | undefined>(undefined);

  const snapshot = read.data;
  const op = snapshot?.queue.operations.find((candidate) => candidate.id === opId);

  // The two tap-time identities (spec §5 r15 amendment): the session
  // principal the batch is judged against, and the registered device id
  // without which e13 404s. A stale pre-amendment cached snapshot may lack
  // worker_id — stated, and the taps are withheld rather than sent to burn.
  const workerId =
    snapshot !== undefined &&
    typeof snapshot.worker_id === "string" &&
    snapshot.worker_id.length > 0
      ? snapshot.worker_id
      : undefined;
  // The device pick is STATE here, not a bare read, because the screen now
  // offers the picker in place (D-118): choosing a tablet has to re-render the
  // panels that were withheld, without a navigation that discards the form.
  const [devicePick, setDevicePick] = useState<DevicePin | undefined>(() => pinnedDevice());
  const device =
    snapshot !== undefined ? resolveDevicePin(snapshot.devices, devicePick) : devicePick;

  // ONE gate for every panel (D-118). Order matters: the reason shown is the
  // one the operator can act on first. "Sending…" is last because it is
  // momentary and the other two are not.
  const gate: TapGate =
    workerId === undefined
      ? { blocked: true, reason: t("production.floor.op.blockedIdentity") }
      : device === undefined
        ? { blocked: true, reason: t("production.floor.op.blockedDevice") }
        : state.phase === "submitting"
          ? { blocked: true, reason: t("production.floor.op.blockedSending") }
          : { blocked: false, reason: undefined };

  const pendingHere = pendingEventsForOp(outbox.items, opId);
  const pendingQty = pendingEventsForOp(outbox.items, opId, "qty_reported");

  // §3.4: THIS DEVICE's view of the op — the snapshot status with its own
  // queued taps folded over it. Offline, Start must flip the controls to
  // in_progress or the tap chain (Start → qty → Complete) dead-ends on a
  // snapshot the network cannot refresh. needs_review never folds (a
  // conflicted event did not happen — effectiveOpStatus's own law).
  const status = op !== undefined ? effectiveOpStatus(op.status, pendingHere) : undefined;

  const materials: StationMaterialExpectation[] = useMemo(() => {
    if (snapshot === undefined || op === undefined) return [];
    // Lines bound to THIS op, plus the WO-level lines of its work order
    // (§5 r15: what the whole order still owes this station's work).
    return snapshot.materials_expected.filter(
      (line) =>
        line.wo_operation_id === opId ||
        (line.wo_operation_id === null && line.work_order_id === op.work_order_id),
    );
  }, [snapshot, op, opId]);

  /**
   * The other operations of THIS work order that the flagger might name as the
   * PRODUCER (§2.11: the payload's `produced_wo_operation_id` must be an
   * operation of the same order — the server refuses anything else with
   * `unknown_reference`, and §9.14 makes a foreign id indistinguishable from a
   * nonexistent one).
   *
   * The station queue is the only source a floor device has, so the list is the
   * OTHER cards of this work order that this station can see. It is a
   * CONVENIENCE, never a requirement: leaving it unset makes the server default
   * to this step, which is §2.11's own "defaults to same op".
   */
  const earlierOpsAtThisWo = useMemo(() => {
    if (snapshot === undefined || op === undefined) return [];
    return snapshot.queue.operations
      .filter((c) => c.work_order_id === op.work_order_id && c.id !== op.id)
      .map((c) => ({ id: c.id, label: `${String(c.seq_no)} · ${c.product_name}` }));
  }, [snapshot, op]);

  const tap = async (
    build: (ctx: TapContext) => ProductionEventOp,
    identity: string,
    scanCode?: string,
    photos?: readonly File[],
  ): Promise<TapOutcome | undefined> => {
    if (workerId === undefined || device === undefined || op === undefined) return undefined;
    reset();
    setPhotoNotSaved(false);
    setOutcomeScan(scanCode);
    const ctx: TapContext = {
      deviceId: device.id,
      workerId,
      workCenterId,
      nowMs: Date.now(),
    };
    // Evidence blobs FIRST, durably, then the flag that references them
    // (D-096: blobs-before-flag, so a death between the two leaves an orphaned
    // blob, never a flag pointing at nothing). A blob that cannot be persisted
    // is the outbox's not-saved case exactly: the tap did NOT land, and the
    // operator must be told so — never a flag quietly queued without the photo
    // the accusation rests on.
    let photoIds: string[] = [];
    if (photos !== undefined && photos.length > 0) {
      try {
        photoIds = await capturePhotos(floorPhotoStore(), photos);
      } catch {
        floorFeedback("error");
        setPhotoNotSaved(true);
        setOutcome({ kind: "not_saved" });
        return { kind: "not_saved" };
      }
    }
    const settlement = await submit(asFloorOp(build(ctx)), undefined, {
      identity,
      ...(photoIds.length > 0 ? { photoIds } : {}),
    });
    const result = tapOutcomeOf(settlement);
    setOutcome(result);
    if (result.kind === "applied") {
      floorFeedback("ok");
      refresh();
    } else if (result.kind === "queued") {
      floorFeedback("warn");
    } else if (result.kind !== "superseded") {
      floorFeedback("error");
    }
    return result;
  };

  const title = op !== undefined ? op.wo_no : t("production.floor.op.title");

  return (
    <FloorScreen
      title={title}
      back={
        <Link to="/floor/station/$workCenterId" params={{ workCenterId }} className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
    >
      <SnapshotNote
        source={read.source}
        cachedAt={read.cachedAt}
        revalidating={read.revalidating}
      />

      {read.isLoading && snapshot === undefined ? <FloorSkeleton rows={4} /> : null}

      {snapshot === undefined && !read.isLoading && read.source === "none" ? (
        <FloorNeedsConnection what={t("production.floor.op.what")} />
      ) : null}

      {snapshot !== undefined && op === undefined ? (
        // §5 e29: the op QR is a shortcut, the station QR is the anchor — an
        // op that is not in THIS station's queue gets an honest redirection,
        // not a guess (the snapshot cannot name the op's own station).
        <FloorEmpty
          title={t("production.floor.op.notHereTitle")}
          body={t("production.floor.op.notHereBody")}
        />
      ) : null}

      {snapshot !== undefined && op !== undefined ? (
        <>
          <p className="floor-card-sub">{op.product_name}</p>
          <div className="station-card-row">
            <span
              className="station-card-status"
              data-status={status ?? op.status}
              data-testid="op-status"
            >
              {t(`production.status.op.${status ?? op.status}`)}
            </span>
            {status !== undefined && status !== op.status ? (
              // The chip shows the DEVICE's view; this says why it may
              // disagree with the wall board: the tap has not synced yet.
              <span className="op-pending-sync" data-testid="op-pending-sync">
                {t("production.floor.pendingSync")}
              </span>
            ) : null}
            <span className="floor-card-measure">
              {t("production.floor.station.progress", {
                reported: floorQty(op.qty_reported, i18n.language),
                planned: floorQty(op.qty_planned, i18n.language, op.uom),
              })}
            </span>
          </div>

          {workerId === undefined ? (
            <p className="floor-hint floor-hint-warn" data-testid="op-no-worker">
              {t("production.floor.op.staleIdentity")}
            </p>
          ) : null}
          {/* THE PICKER, HERE (D-118). Two registered tablets and no pin is the
              ordinary state of a two-station plant reached by a `/q/op/` scan,
              a deep link or a browser restart, and the fix for it is one tap —
              so the tap belongs on this screen, not behind a link to another
              one that throws away whatever the operator has already keyed. */}
          {device === undefined ? (
            <div data-testid="op-no-device">
              <DeviceIdentity
                devices={snapshot.devices}
                pick={devicePick}
                onPick={(pin) => {
                  pinDevice(pin);
                  setDevicePick(pin);
                }}
              />
            </div>
          ) : null}

          {/* §2.2's QC HOLD, ABOVE THE TAPS (04-C-5). This is the one guard
              hint that is not about a form further down the screen: while the
              hold is set, §3.2 refuses Start, Resume, Complete AND every
              quantity report, so the operator's whole reason for being at this
              machine is blocked. It sits with the identity warnings rather than
              with the completion hints because it is a "do not start" fact, not
              a "before you complete" one. Still a hint and never a client-side
              block — §3.2's guard is the authority, and a tap that goes through
              lands in review with `qc_hold` naming why. Only e32 (office S2,
              behind `production.qc_hold.release`) clears it. */}
          {op.qc_hold ? (
            <p className="floor-hint floor-hint-warn" data-testid="op-guard-qc-hold">
              {t("production.floor.op.guardQcHold")}
            </p>
          ) : null}
          {/* §3.2's rework re-open. A re-opened step reads `in_progress`
              exactly like a step running for the first time, and the difference
              is what the work is BOOKED as: reports default `is_rework = true`
              and labor lands `entry_kind = 'rework'`. Telling the operator
              after the fact would be telling them how they were paid after they
              were paid. */}
          {op.rework_reopened_at !== null ? (
            <p className="floor-hint" data-testid="op-rework-note">
              {t("production.floor.op.reworkNote")}
            </p>
          ) : null}

          {/* WHICH TAPS ARE STILL ON THE DEVICE (D-121). This note counted them
              and stopped there — and this is the screen where a count is most
              expensive, because the numpad that would re-enter the quantity is
              400 px below it. An operator who reported 420 offline, saw
              "1 tap(s) for this step are still on this device" and could not
              tell WHICH tap it was has one safe-feeling move available: report
              it again. That is a second `qty_reported` event against one
              operation in an append-only ledger — 840 booked, a yield figure
              that is wrong on the wall board, and a supervisor unpicking it at
              a count days later. The list costs four lines and answers the
              question the count provokes. */}
          {pendingHere.length > 0 ? (
            <div className="op-pending-note" data-testid="op-pending-note">
              <p>{t("production.floor.op.pendingHere", { count: pendingHere.length })}</p>
              <ul className="op-pending-list" data-testid="op-pending-list">
                {pendingHere.map((item) => {
                  const label = itemLabel(item);
                  return (
                    <li key={item.clientKey} data-testid="op-pending-item">
                      {t(label.key, label.params)}
                      {" · "}
                      <PendingWhen at={item.createdAt} />
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <TapResult outcome={outcome} scanCode={outcomeScan} />
          {state.phase === "not_saved" || photoNotSaved ? <FloorNotSaved /> : null}

          <OpActions
            status={status ?? op.status}
            gate={gate}
            onStart={() => void tap((ctx) => opStartedOp(ctx, opId), `${op.wo_no} · Start`)}
            onPause={() => void tap((ctx) => opPausedOp(ctx, opId), `${op.wo_no} · Pause`)}
            onResume={() => void tap((ctx) => opResumedOp(ctx, opId), `${op.wo_no} · Resume`)}
            onComplete={() => void tap((ctx) => opCompletedOp(ctx, opId), `${op.wo_no} · Complete`)}
          />

          {/* §3.2 guard hints, BEFORE the tap, never a block. */}
          {Number(op.qty_reported) <= 0 && pendingQty.length === 0 ? (
            <p className="floor-hint floor-hint-warn" data-testid="op-guard-qty">
              {t("production.floor.op.guardNoQty")}
            </p>
          ) : null}
          {op.requires_measured_capture ? (
            // 04-C-2: the hint now points at the FORM BELOW — the control the
            // 04-B text promised. Still a hint, never a client-side block.
            <p className="floor-hint floor-hint-warn" data-testid="op-guard-calibration">
              {t("production.floor.op.guardCalibration")}
            </p>
          ) : null}
          {op.requires_serial_capture ? (
            <p className="floor-hint floor-hint-warn" data-testid="op-guard-serial">
              {t("production.floor.op.guardSerial")}
            </p>
          ) : null}

          <QtyPanel
            gate={gate}
            scrapReasons={snapshot.scrap_reasons}
            locale={i18n.language}
            precision={op.uom_decimal_precision}
            onReport={(qty) =>
              void tap(
                (ctx) => qtyReportedOp(ctx, opId, qty),
                `${op.wo_no} · ${t("production.floor.op.qtyIdentity")}`,
              )
            }
          />

          <MaterialPanel
            gate={gate}
            materials={materials}
            locale={i18n.language}
            onScanIssue={(input) =>
              void tap(
                (ctx) => materialScanOp(ctx, opId, input),
                `${op.wo_no} · ${t("production.floor.op.materialIdentity")}`,
                input.scanCode,
              )
            }
            onManualIssue={(input) =>
              void tap(
                (ctx) => materialManualOp(ctx, opId, input),
                `${op.wo_no} · ${t("production.floor.op.materialIdentity")}`,
              )
            }
            onScanReturn={(input) =>
              void tap(
                (ctx) => materialScanReturnOp(ctx, opId, input),
                `${op.wo_no} · ${t("production.floor.op.returnIdentity")}`,
                input.scanCode,
              )
            }
            onManualReturn={(input) =>
              void tap(
                (ctx) => materialManualReturnOp(ctx, opId, input),
                `${op.wo_no} · ${t("production.floor.op.returnIdentity")}`,
              )
            }
          />

          {op.requires_measured_capture ? (
            <CalibrationPanel
              gate={gate}
              onRecord={(input) =>
                void tap(
                  (ctx) => calibrationRecordedOp(ctx, opId, input),
                  `${op.wo_no} · ${t("production.floor.op.calibrationIdentity")}`,
                )
              }
            />
          ) : null}

          <SerialCapturePanel
            required={op.requires_serial_capture}
            gate={gate}
            defaultUom={op.uom}
            defaultPrecision={op.uom_decimal_precision}
            locale={i18n.language}
            onCapture={(input) =>
              void tap(
                (ctx) => serialLotCapturedOp(ctx, opId, input),
                `${op.wo_no} · ${t("production.floor.op.serialIdentity")}`,
                input.scanCode,
              )
            }
          />

          <DowntimePanel
            gate={gate}
            reasons={snapshot.downtime_reasons}
            openDowntime={
              // The server's open stoppages PLUS any this device opened that
              // has not drained yet — offline the first list is empty and the
              // End control would otherwise not exist (D-093 done-gate: a full
              // factory day, offline, from a phone).
              snapshot.open_downtime.length > 0
                ? snapshot.open_downtime
                : pendingOpenDowntimeForOp(outbox.items, opId, (item) =>
                    new Date(item.createdAt).toISOString(),
                  )
            }
            locale={i18n.language}
            onStart={(reasonCode) =>
              void tap(
                (ctx) => downtimeStartedOp(ctx, { reasonCode, opId }),
                `${op.wo_no} · ${t("production.floor.op.downtimeIdentity")}`,
              )
            }
            onEnd={(startedClientEventId) =>
              void tap(
                (ctx) => downtimeEndedOp(ctx, startedClientEventId),
                `${op.wo_no} · ${t("production.floor.op.downtimeEndIdentity")}`,
              ).then((result) => {
                // D-299 round 3, R6 (web half). The office can end a
                // stoppage now (§5 e35), and nothing pushes that to this
                // tablet: its snapshot keeps the row, with an End that can
                // only land on a row the server no longer holds open. A
                // server with R6 answers that tap as a benign duplicate,
                // which settles APPLIED and refreshes above. A server
                // without it (a deploy in flight, an older build) answers
                // `unknown_reference` — the server has no open stoppage for
                // that opening tap — and a conflicted tap refreshes nothing
                // by rule. This one does, because here the conflict IS the
                // news that the snapshot is stale: without the refresh the
                // row stays with its End, and every further tap files
                // another rejection on S7 for somebody to discard.
                if (result?.kind === "conflicted" && result.reason === "unknown_reference") {
                  refresh();
                }
              })
            }
          />

          {/* §3.3 step 10 — THE FLAG (04-C-5). The pending note that stood
              here through 04-B/C-1/C-2/C-3 has RETIRED with its last clause:
              every §5.1 control the note promised is now a panel on this
              screen.

              THE OPERATOR NEVER NAMES THE SHIFT. The panel offers a quantity, a
              reason and — optionally — WHICH EARLIER STEP made it; the server
              walks the quantity timeline for the rest. That is not a
              simplification for the phone: `produced_shift_instance_id` decides
              whose scoreboard takes the hit, and a device that could set it
              would be the gaming vector this whole mechanic exists to close. */}
          <DefectFlagPanel
            gate={gate}
            scrapReasons={snapshot.scrap_reasons}
            locale={i18n.language}
            precision={op.uom_decimal_precision}
            earlierOps={earlierOpsAtThisWo}
            onFlag={(input, photos) =>
              void tap(
                (ctx) => defectHandoffFlaggedOp(ctx, opId, input),
                `${op.wo_no} · ${t("production.floor.op.defectIdentity")}`,
                undefined,
                photos,
              )
            }
          />
        </>
      ) : null}
    </FloorScreen>
  );
}

function TapResult({
  outcome,
  scanCode,
}: {
  outcome: TapOutcome | undefined;
  /** The raw scan the refused tap carried, when it carried one. */
  scanCode?: string | undefined;
}) {
  const { t } = useTranslation();
  if (outcome === undefined) return null;
  switch (outcome.kind) {
    case "applied":
      return (
        <p className="floor-hint" role="status" data-testid="op-applied">
          {t("production.floor.op.applied")}
        </p>
      );
    case "queued":
      return (
        <p className="floor-hint" role="status" data-testid="op-queued">
          {t("production.floor.op.queued")}
        </p>
      );
    case "conflicted":
      // The D-080 outcome: the server refused the fact and persisted it into
      // the office conflict queue; the record sits in the tray as
      // needs_review. Loud, with the server's own reason.
      //
      // EXCEPT for the one refusal an operator can actually fix at the machine:
      // a scan that resolved to nothing. `unknown_reference` is a server enum
      // and tells a worker in gloves precisely nothing — so when the refused
      // tap carried a scan, the message names THE CODE and the next action
      // (check the label, or type the engraved serial). The tap is still in the
      // tray either way; the difference is whether the operator can retry.
      if (outcome.reason === "unknown_reference" && scanCode !== undefined) {
        return (
          <p className="op-review-note" role="alert" data-testid="op-scan-unresolved">
            {t("production.floor.op.scanUnresolved", { code: scanCode })}{" "}
            <Link to="/floor/tray">{t("production.floor.op.openTray")}</Link>
          </p>
        );
      }
      return (
        <p className="op-review-note" role="alert" data-testid="op-conflicted">
          {t("production.floor.op.conflicted", { reason: outcome.reason })}{" "}
          <Link to="/floor/tray">{t("production.floor.op.openTray")}</Link>
        </p>
      );
    case "needs_attention":
      return (
        <p className="op-review-note" role="alert" data-testid="op-attention">
          {t("production.floor.op.needsAttention")}{" "}
          <Link to="/floor/tray">{t("production.floor.op.openTray")}</Link>
        </p>
      );
    case "superseded":
    case "not_saved":
      return null;
  }
}

/** "14 min ago", in the SAME buckets the tray and the snapshot note use — a
 * queued tap an operator is deciding whether to redo must not read one way on
 * the op screen and another on the tray they are about to open. */
function PendingWhen({ at }: { at: number }) {
  const { t } = useTranslation();
  const age = snapshotAgeParts(Math.max(0, Date.now() - at));
  return <>{t(age.key, { count: age.n })}</>;
}

function OpActions({
  status,
  gate,
  onStart,
  onPause,
  onResume,
  onComplete,
}: {
  status: string;
  gate: TapGate;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onComplete: () => void;
}) {
  const { t } = useTranslation();
  const disabled = gate.blocked;
  const reason = gate.reason;
  if (status === "completed" || status === "skipped") {
    return (
      <p className="floor-hint" data-testid="op-done-note">
        {t("production.floor.op.doneNote")}
      </p>
    );
  }
  return (
    <div className="op-actions" data-testid="op-actions">
      {status === "ready" || status === "queued" ? (
        <FloorButton
          onClick={onStart}
          variant="primary"
          disabled={disabled}
          disabledReason={reason}
          testId="op-start"
        >
          {t("production.floor.op.start")}
        </FloorButton>
      ) : null}
      {status === "paused" ? (
        <FloorButton
          onClick={onResume}
          variant="primary"
          disabled={disabled}
          disabledReason={reason}
          testId="op-resume"
        >
          {t("production.floor.op.resume")}
        </FloorButton>
      ) : null}
      {status === "in_progress" ? (
        <FloorButton
          onClick={onPause}
          disabled={disabled}
          disabledReason={reason}
          testId="op-pause"
        >
          {t("production.floor.op.pause")}
        </FloorButton>
      ) : null}
      {status === "in_progress" || status === "paused" ? (
        <FloorButton
          onClick={onComplete}
          variant="danger"
          disabled={disabled}
          disabledReason={reason}
          testId="op-complete"
        >
          {t("production.floor.op.complete")}
        </FloorButton>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// §3.3 step 10 — the DEFECT FLAG (04-C-5)
// ---------------------------------------------------------------------------
//
// The worker opens a drum from an earlier shift and finds it broken. Three
// facts leave the phone — how much, why, and (optionally) which earlier step
// made it — and the server decides the rest.
//
// WHY THERE IS NO SHIFT PICKER HERE, said once where somebody might add one:
// the producing SHIFT is what the scoreboard mechanic moves a number onto, the
// server resolves it by walking §2.4's timeline backwards from this tap, and a
// control that let the floor choose it would hand the gaming problem straight
// back. The operator is asked what they can SEE.
//
// THE PHOTO HALF IS A REAL CONTROL AS OF 2026-08-08 (D-096), and the note that
// stood in its place has RETIRED with the condition it described. The capture
// is `<input capture="environment">` behind a glove-sized button — the OS
// camera, not a re-implementation of one — and the blobs ride the durable
// photo queue beside the outbox (photo-store.ts): the FLAG is still one tap,
// online or off, and it is the DRAIN that runs the three-call upload
// (`POST /v1/files` → presigned PUT → store) on reconnect and binds the
// resulting `photo_file_ids` into the payload before the event is sent
// (§2.11's "the outbox enqueues the upload first"). Size/type are refused at
// the camera with the SERVER's own limits — a queued blob the registry would
// 422 is a guaranteed stuck tray entry hours later, when the drum is gone.

function DefectFlagPanel({
  gate,
  scrapReasons,
  locale,
  precision,
  earlierOps,
  onFlag,
}: {
  gate: TapGate;
  scrapReasons: readonly { code: string; name_i18n: Record<string, string | undefined> }[];
  locale: string;
  /** The op UoM's entry precision (D-091) — a defective length is measured in
   * the same unit the work is. */
  precision: number | undefined;
  earlierOps: readonly { id: string; label: string }[];
  onFlag: (
    input: {
      qtyDefective: string;
      scrapReasonCode: string;
      producedWoOperationId?: string | undefined;
      notes?: string | undefined;
    },
    photos: readonly File[],
  ) => void;
}) {
  const { t } = useTranslation();
  const disabled = gate.blocked;
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("0");
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [producedOp, setProducedOp] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [photos, setPhotos] = useState<readonly { file: File; url: string }[]>([]);
  const photoInput = useRef<HTMLInputElement | null>(null);

  // Preview object URLs are browser resources, not garbage-collected state:
  // revoked one by one as photos are retaken, and — via the ref, so the
  // cleanup sees the CURRENT list rather than re-running on every change and
  // revoking previews still on screen — all together on unmount.
  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(
    () => () => {
      for (const photo of photosRef.current) URL.revokeObjectURL(photo.url);
    },
    [],
  );

  const addPhotos = (files: FileList | null): void => {
    if (files === null || files.length === 0) return;
    const added: { file: File; url: string }[] = [];
    for (const file of Array.from(files)) {
      // The SERVER's own limits, enforced BEFORE the blob is queued (D-096):
      // the pair comes from the contract, the same source the registry builds
      // its refusals from, so the two answers cannot drift apart.
      const refused = floorPhotoProblem(file);
      if (refused !== undefined) {
        setProblem(refused === "too_large" ? "photo_too_large" : "photo_wrong_type");
        return;
      }
      added.push({ file, url: URL.createObjectURL(file) });
    }
    if (photos.length + added.length > MAX_FLAG_PHOTOS) {
      for (const photo of added) URL.revokeObjectURL(photo.url);
      setProblem("photo_limit");
      return;
    }
    setProblem(undefined);
    setPhotos([...photos, ...added]);
  };

  const removePhoto = (index: number): void => {
    const photo = photos[index];
    if (photo !== undefined) URL.revokeObjectURL(photo.url);
    setPhotos(photos.filter((_, i) => i !== index));
    setProblem(undefined);
  };

  const clearForm = (): void => {
    setOpen(false);
    setQty("0");
    setReason(undefined);
    setProducedOp("");
    setNotes("");
    // The Files are already handed off (or abandoned); only the previews are
    // ours to release.
    for (const photo of photos) URL.revokeObjectURL(photo.url);
    setPhotos([]);
  };

  const submit = (): void => {
    const invalid = defectFlagProblem(qty, reason);
    // `defectFlagProblem` returning undefined IS the proof `reason` is set —
    // §2.11 makes the cause code NOT NULL and the helper is the client mirror
    // of that. The re-check narrows it for the type system rather than
    // asserting past it.
    if (invalid !== undefined || reason === undefined) {
      setProblem(invalid ?? "reason_required");
      return;
    }
    setProblem(undefined);
    onFlag(
      {
        qtyDefective: normalizeQty(qty),
        scrapReasonCode: reason,
        ...(producedOp === "" ? {} : { producedWoOperationId: producedOp }),
        ...(notes.trim() === "" ? {} : { notes: notes.trim() }),
      },
      photos.map((photo) => photo.file),
    );
    clearForm();
  };

  if (!open) {
    return (
      <section className="floor-panel" data-testid="op-defect-panel">
        <FloorButton
          onClick={() => {
            setOpen(true);
          }}
          disabled={disabled}
          disabledReason={gate.reason}
          testId="op-defect-open"
        >
          {t("production.floor.op.flagDefect")}
        </FloorButton>
      </section>
    );
  }

  return (
    <section className="floor-panel" data-testid="op-defect-panel">
      <h2 className="floor-panel-head">{t("production.floor.op.flagDefect")}</h2>

      <span className="op-field-label">{t("production.floor.op.defectQty")}</span>
      <QtyStepper
        value={qty}
        onChange={setQty}
        testId="op-defect-qty"
        precision={precision}
        locale={locale}
      />

      <span className="op-field-label">{t("production.floor.op.scrapReason")}</span>
      {scrapReasons.length === 0 ? (
        <p className="floor-hint floor-hint-warn" data-testid="op-defect-no-reasons">
          {t("production.floor.op.noScrapReasons")}
        </p>
      ) : (
        <div className="reason-grid" data-testid="op-defect-reason-grid">
          {scrapReasons.map((cause) => (
            <button
              key={cause.code}
              type="button"
              className="floor-btn"
              aria-pressed={reason === cause.code}
              data-testid={`op-defect-reason-${cause.code}`}
              onClick={() => {
                setReason(cause.code);
              }}
            >
              {localizedName(cause.name_i18n, locale, cause.code)}
            </button>
          ))}
        </div>
      )}

      {/* WHICH STEP MADE IT — optional, and the default is SAID rather than
          left as an empty select the operator has to interpret. §2.11's
          "defaults to same op" is exactly what leaving this alone does.

          WHEN THERE IS NOTHING TO PICK, THE DEFAULT IS STILL STATED. The
          picker's source is the station queue, which §5 r15 scopes to ONE work
          centre — so a step of this order at ANOTHER station is never offered,
          and on those flags the operator was choosing nothing while the server
          silently attributed the defect to the step they are standing at
          (adversarial review, 2026-08-07). That attribution decides WHOSE
          scoreboard takes the hit, which makes an unstated default the one
          thing this form must not have. Widening the picker beyond the station
          needs the queue wire to carry the order's whole routing and its
          offline cache to hold it — successor work, named in spec 04 §3.3
          step 10 and D-088 ruling 12, not smuggled in here. */}
      {earlierOps.length === 0 ? (
        <p className="floor-hint" data-testid="op-defect-produced-default">
          {t("production.floor.op.defectProducedHereOnly")}
        </p>
      ) : null}
      {earlierOps.length > 0 ? (
        <>
          <span className="op-field-label">{t("production.floor.op.defectProducedAt")}</span>
          <select
            className="floor-select"
            data-testid="op-defect-produced-op"
            value={producedOp}
            onChange={(e) => {
              setProducedOp(e.target.value);
            }}
          >
            <option value="">{t("production.floor.op.defectProducedHere")}</option>
            {earlierOps.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </>
      ) : null}

      <span className="op-field-label">{t("production.floor.op.defectNotes")}</span>
      <input
        className="floor-input"
        type="text"
        data-testid="op-defect-notes"
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
        }}
      />

      {/* THE EVIDENCE PHOTO (§3.3 step 10's third fact, D-096). The photo is
          what settles a day/night dispute — retro-attribution moves numbers
          between shifts and somebody's piece rate follows, and an accusation
          with no picture is the argument a shift boss wins by shouting. The
          capture goes to the OS camera (`capture="environment"`); the blob is
          queued durably beside the flag and uploads on reconnect. Optional,
          exactly as the wire's `photo_file_ids: []` is legal — a worker whose
          camera is broken must still be able to flag. */}
      <span className="op-field-label">{t("production.floor.op.defectPhoto")}</span>
      {photos.length > 0 ? (
        <div className="op-photo-strip" data-testid="op-defect-photo-strip">
          {photos.map((photo, index) => (
            <figure key={photo.url} className="op-photo" data-testid={`op-defect-photo-${index}`}>
              <img
                className="op-photo-preview"
                src={photo.url}
                alt={t("production.floor.op.defectPhotoPreview", { n: index + 1 })}
                data-testid={`op-defect-photo-preview-${index}`}
              />
              <FloorButton
                onClick={() => {
                  removePhoto(index);
                }}
                testId={`op-defect-photo-retake-${index}`}
              >
                {t("production.floor.op.defectPhotoRetake")}
              </FloorButton>
            </figure>
          ))}
        </div>
      ) : null}
      <input
        ref={photoInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        capture="environment"
        multiple
        className="op-photo-input"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="op-defect-photo-input"
        onChange={(e) => {
          addPhotos(e.target.files);
          // Re-arm: the same photo re-taken must fire onChange again.
          e.target.value = "";
        }}
      />
      <FloorButton
        onClick={() => photoInput.current?.click()}
        disabled={disabled || photos.length >= MAX_FLAG_PHOTOS}
        disabledReason={
          disabled
            ? gate.reason
            : t("production.floor.op.defectPhotoLimit", { max: MAX_FLAG_PHOTOS })
        }
        testId="op-defect-photo-add"
      >
        {photos.length === 0
          ? t("production.floor.op.defectPhotoAdd")
          : t("production.floor.op.defectPhotoAddAnother")}
      </FloorButton>
      <p className="floor-hint" data-testid="op-defect-photo-hint">
        {t("production.floor.op.defectPhotoHint")}
      </p>

      {problem === "nothing_flagged" ? (
        <p className="floor-hint floor-hint-warn" role="alert" data-testid="op-defect-problem">
          {t("production.floor.op.defectNothingFlagged")}
        </p>
      ) : null}
      {problem === "reason_required" ? (
        <p className="floor-hint floor-hint-warn" role="alert" data-testid="op-defect-problem">
          {t("production.floor.op.reasonRequired")}
        </p>
      ) : null}
      {problem === "photo_too_large" ? (
        <p className="floor-hint floor-hint-warn" role="alert" data-testid="op-defect-problem">
          {t("production.floor.op.defectPhotoTooLarge")}
        </p>
      ) : null}
      {problem === "photo_wrong_type" ? (
        <p className="floor-hint floor-hint-warn" role="alert" data-testid="op-defect-problem">
          {t("production.floor.op.defectPhotoWrongType")}
        </p>
      ) : null}
      {problem === "photo_limit" ? (
        <p className="floor-hint floor-hint-warn" role="alert" data-testid="op-defect-problem">
          {t("production.floor.op.defectPhotoLimit", { max: MAX_FLAG_PHOTOS })}
        </p>
      ) : null}

      <FloorButton
        onClick={submit}
        variant="primary"
        disabled={disabled}
        disabledReason={gate.reason}
        testId="op-defect-send"
      >
        {t("production.floor.op.defectSend")}
      </FloorButton>
      <FloorButton
        onClick={() => {
          clearForm();
          setProblem(undefined);
        }}
        testId="op-defect-cancel"
      >
        {t("production.floor.op.cancel")}
      </FloorButton>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Qty stepper + scrap-reason grid
// ---------------------------------------------------------------------------

function QtyPanel({
  gate,
  scrapReasons,
  locale,
  precision,
  onReport,
}: {
  gate: TapGate;
  scrapReasons: readonly { code: string; name_i18n: Record<string, string | undefined> }[];
  locale: string;
  /** The op UoM's entry precision (`uom_decimal_precision`, D-091).
   * `undefined` = a stale pre-D-091 cached snapshot; the stepper falls back
   * to the 3-dp wire maximum. */
  precision: number | undefined;
  onReport: (qty: { good: string; scrap: string; scrapReasonCode?: string | undefined }) => void;
}) {
  const { t } = useTranslation();
  const disabled = gate.blocked;
  const [good, setGood] = useState("0");
  const [scrap, setScrap] = useState("0");
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const scrapPositive = Number(scrap) > 0;

  const report = (): void => {
    const invalid = qtyReportProblem(good, scrap, reason);
    if (invalid !== undefined) {
      setProblem(invalid);
      return;
    }
    setProblem(undefined);
    onReport({
      good: normalizeQty(good),
      scrap: normalizeQty(scrap),
      ...(scrapPositive && reason !== undefined ? { scrapReasonCode: reason } : {}),
    });
    setGood("0");
    setScrap("0");
    setReason(undefined);
  };

  return (
    <section className="floor-panel" data-testid="op-qty-panel">
      <h2 className="floor-panel-head">{t("production.floor.op.reportQty")}</h2>

      <span className="op-field-label">{t("production.floor.op.qtyGood")}</span>
      <QtyStepper
        value={good}
        onChange={setGood}
        testId="op-qty-good"
        precision={precision}
        locale={locale}
      />

      <span className="op-field-label">{t("production.floor.op.qtyScrap")}</span>
      <QtyStepper
        value={scrap}
        onChange={setScrap}
        testId="op-qty-scrap"
        precision={precision}
        locale={locale}
      />

      {scrapPositive ? (
        <>
          <span className="op-field-label">{t("production.floor.op.scrapReason")}</span>
          {scrapReasons.length === 0 ? (
            <p className="floor-hint floor-hint-warn" data-testid="op-no-reasons">
              {t("production.floor.op.noScrapReasons")}
            </p>
          ) : (
            <div className="reason-grid" data-testid="op-reason-grid">
              {scrapReasons.map((cause) => (
                <button
                  key={cause.code}
                  type="button"
                  className="floor-btn"
                  aria-pressed={reason === cause.code}
                  data-testid={`op-reason-${cause.code}`}
                  onClick={() => {
                    setReason(cause.code);
                  }}
                >
                  {localizedName(cause.name_i18n, locale, cause.code)}
                </button>
              ))}
            </div>
          )}
        </>
      ) : null}

      {problem === "nothing_reported" ? (
        <p className="floor-hint floor-hint-warn" role="alert" data-testid="op-qty-problem">
          {t("production.floor.op.nothingReported")}
        </p>
      ) : null}
      {problem === "scrap_reason_required" ? (
        <p className="floor-hint floor-hint-warn" role="alert" data-testid="op-qty-problem">
          {t("production.floor.op.reasonRequired")}
        </p>
      ) : null}

      <FloorButton
        onClick={report}
        variant="primary"
        disabled={disabled}
        disabledReason={gate.reason}
        testId="op-report-qty"
      >
        {t("production.floor.op.reportButton")}
      </FloorButton>
    </section>
  );
}

// The Stepper that stood here through 04-B/C was the INTEGER half of §4 row
// 10's "qty stepper + numpad": ±1 one-tap, decimals only via the OS keyboard.
// It moved to `../components/qty-stepper` (D-091) and grew the numpad half —
// glove-sized digit keys, a locale-labelled decimal key, the unit's own
// precision (`uom_decimal_precision`) instead of a hardcoded 3. `normalizeQty`
// moved with it to `../lib/qty-entry`.

// ---------------------------------------------------------------------------
// Downtime button + reason grid + open-downtime indicator (S10, 04-C-1)
// ---------------------------------------------------------------------------

/**
 * The S10 downtime control (spec 04 §4 row 10 — "Log downtime (reason picker
 * from downtime_reasons)"). The grid renders the ACTIVE §2.6 catalog from the
 * e15 snapshot; the open-downtime block lists the station's open §2.7 rows
 * (also snapshot-served, so both survive a dead zone) with an End action that
 * composes `downtime_ended` from the row's opening client_event_id. A row
 * whose opening tap the snapshot cannot name (opened by a non-floor surface)
 * shows WITHOUT an End button rather than sending a tap the server must
 * refuse.
 */
function DowntimePanel({
  gate,
  reasons,
  openDowntime,
  locale,
  onStart,
  onEnd,
}: {
  gate: TapGate;
  reasons: readonly FloorDowntimeReason[];
  openDowntime: readonly FloorOpenDowntime[];
  locale: string;
  onStart: (reasonCode: string) => void;
  onEnd: (startedClientEventId: string) => void;
}) {
  const { t } = useTranslation();
  const disabled = gate.blocked;
  const [picking, setPicking] = useState(false);
  const reasonByCode = useMemo(() => new Map(reasons.map((r) => [r.code, r])), [reasons]);

  return (
    <section className="floor-panel" data-testid="op-downtime-panel">
      <h2 className="floor-panel-head">{t("production.floor.op.downtime")}</h2>

      {openDowntime.length > 0 ? (
        <div data-testid="op-open-downtime">
          <p className="floor-hint floor-hint-warn" data-testid="op-open-downtime-note">
            {t("production.floor.op.downtimeOpenNote", { count: openDowntime.length })}
          </p>
          <ul className="floor-cards">
            {openDowntime.map((d) => {
              // Bound to a const so the closure below narrows: a row without
              // an opening tap id gets NO End button (never a tap the server
              // must refuse — it was opened by a non-floor surface).
              const openingTapId = d.started_client_event_id;
              return (
                <li key={d.downtime_log_id} className="floor-card">
                  <p className="floor-card-title">
                    {localizedName(
                      reasonByCode.get(d.reason_code)?.name_i18n,
                      locale,
                      d.reason_code,
                    )}
                  </p>
                  <p className="floor-card-sub">
                    {t("production.floor.op.downtimeSince", {
                      time: new Date(d.started_at).toLocaleTimeString(locale, {
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                    })}
                  </p>
                  {openingTapId !== null ? (
                    <FloorButton
                      onClick={() => {
                        onEnd(openingTapId);
                      }}
                      variant="primary"
                      disabled={disabled}
                      disabledReason={gate.reason}
                      testId={`op-downtime-end-${d.downtime_log_id}`}
                    >
                      {t("production.floor.op.downtimeEnd")}
                    </FloorButton>
                  ) : (
                    <p className="floor-hint" data-testid="op-downtime-no-end">
                      {t("production.floor.op.downtimeNoEnd")}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {!picking ? (
        <FloorButton
          onClick={() => {
            setPicking(true);
          }}
          disabled={disabled}
          disabledReason={gate.reason}
          testId="op-downtime-button"
        >
          {t("production.floor.op.downtimeButton")}
        </FloorButton>
      ) : reasons.length === 0 ? (
        // An empty grid states WHY it is empty: the tenant's catalog holds no
        // active reason — never a silent nothing that reads as "no downtime
        // here" (the 04-B pending note is retired; this is a data fact now).
        <p className="floor-hint floor-hint-warn" data-testid="op-downtime-no-reasons">
          {t("production.floor.op.downtimeNoReasons")}
        </p>
      ) : (
        <div className="reason-grid" data-testid="op-downtime-reason-grid">
          {reasons.map((reason) => (
            <button
              key={reason.code}
              type="button"
              className="floor-btn"
              data-testid={`op-downtime-reason-${reason.code}`}
              onClick={() => {
                setPicking(false);
                onStart(reason.code);
              }}
            >
              {localizedName(reason.name_i18n, locale, reason.code)}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Material scan issue
// ---------------------------------------------------------------------------

function MaterialPanel({
  gate,
  materials,
  locale,
  onScanIssue,
  onManualIssue,
  onScanReturn,
  onManualReturn,
}: {
  gate: TapGate;
  materials: readonly StationMaterialExpectation[];
  locale: string;
  onScanIssue: (input: { scanCode: string; qty: string; uom: string }) => void;
  onManualIssue: (input: { componentProductId: string; qty: string; uom: string }) => void;
  onScanReturn: (input: { scanCode: string; qty: string; uom: string }) => void;
  onManualReturn: (input: { componentProductId: string; qty: string; uom: string }) => void;
}) {
  const { t } = useTranslation();
  const disabled = gate.blocked;
  const [scanned, setScanned] = useState<string | undefined>(undefined);
  const [qty, setQty] = useState("0");
  // D-081: the RETURN is the inverse of the scan issue ON THE SAME SCREEN —
  // same scan, same stepper, same expected lines; the mode decides the sign
  // (the builders own it; the worker always types a magnitude).
  const [mode, setMode] = useState<"issue" | "return">("issue");
  const uoms = useMemo(() => Array.from(new Set(materials.map((m) => m.uom))), [materials]);
  const [uom, setUom] = useState<string | undefined>(undefined);
  const effectiveUom = uom ?? uoms[0];
  // The no-scan fallback targets ONE expected line (§5.1's
  // component_product_id arm) — never a free-typed product id.
  const [manualLine, setManualLine] = useState<StationMaterialExpectation | undefined>(undefined);
  // D-091: the entry precision follows the TARGET line's unit — the manual
  // line's own, or (scan path) the expected line matching the chosen UoM.
  // `undefined` (a stale pre-D-091 cache, or a scan with no expected line to
  // borrow from) falls back to the 3-dp wire maximum inside the stepper.
  const precisionByUom = useMemo(
    () => new Map(materials.map((m) => [m.uom, m.uom_decimal_precision])),
    [materials],
  );
  const qtyPrecision =
    manualLine !== undefined
      ? manualLine.uom_decimal_precision
      : effectiveUom !== undefined
        ? precisionByUom.get(effectiveUom)
        : undefined;

  const issue = (): void => {
    const value = normalizeQty(qty);
    if (!(Number(value) > 0)) return;
    if (scanned !== undefined && effectiveUom !== undefined) {
      (mode === "return" ? onScanReturn : onScanIssue)({
        scanCode: scanned,
        qty: value,
        uom: effectiveUom,
      });
      setScanned(undefined);
      setQty("0");
      return;
    }
    if (manualLine !== undefined) {
      (mode === "return" ? onManualReturn : onManualIssue)({
        componentProductId: manualLine.component_product_id,
        qty: value,
        uom: manualLine.uom,
      });
      setManualLine(undefined);
      setQty("0");
    }
  };

  return (
    <section className="floor-panel" data-testid="op-material-panel">
      <h2 className="floor-panel-head">{t("production.floor.op.material")}</h2>

      <div
        className="floor-mode-toggle"
        role="group"
        aria-label={t("production.floor.op.materialMode")}
      >
        <button
          type="button"
          className="floor-btn"
          aria-pressed={mode === "issue"}
          data-testid="op-material-mode-issue"
          onClick={() => {
            setMode("issue");
          }}
        >
          {t("production.floor.op.modeIssue")}
        </button>
        <button
          type="button"
          className="floor-btn"
          aria-pressed={mode === "return"}
          data-testid="op-material-mode-return"
          onClick={() => {
            setMode("return");
          }}
        >
          {t("production.floor.op.modeReturn")}
        </button>
      </div>

      {materials.length > 0 ? (
        <ul className="floor-cards" data-testid="op-material-expected">
          {materials.map((line) => (
            <li
              key={`${line.component_product_id}:${line.wo_operation_id ?? "wo"}`}
              className="floor-card"
            >
              <p className="floor-card-title">{line.product_name}</p>
              <p className="floor-card-sub">
                {t("production.floor.op.materialLine", {
                  issued: floorQty(line.qty_issued, locale),
                  planned: floorQty(line.qty_planned, locale),
                  uom: unitSymbol(line.uom, { locale, count: Number(line.qty_planned) }),
                })}
              </p>
              <button
                type="button"
                className="floor-btn"
                data-testid="op-material-manual"
                onClick={() => {
                  setManualLine(line);
                  setScanned(undefined);
                }}
              >
                {mode === "return"
                  ? t("production.floor.op.returnNoScan")
                  : t("production.floor.op.issueNoScan")}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="floor-hint">{t("production.floor.op.noMaterials")}</p>
      )}

      <ScanInput
        autoFocus={false}
        onScan={(payload) => {
          setScanned(payload.trim());
          setManualLine(undefined);
        }}
        label={t("production.floor.op.scanLabel")}
        placeholder={t("production.floor.op.scanPlaceholder")}
      />

      {scanned !== undefined || manualLine !== undefined ? (
        <div data-testid="op-material-form">
          <p className="floor-hint" data-testid="op-material-target">
            {scanned !== undefined
              ? t(
                  mode === "return"
                    ? "production.floor.op.scanReturnTarget"
                    : "production.floor.op.scanTarget",
                  { code: scanned },
                )
              : t(
                  mode === "return"
                    ? "production.floor.op.manualReturnTarget"
                    : "production.floor.op.manualTarget",
                  { name: manualLine?.product_name ?? "" },
                )}
          </p>
          <span className="op-field-label">{t("production.floor.op.materialQty")}</span>
          <QtyStepper
            value={qty}
            onChange={setQty}
            testId="op-material-qty"
            precision={qtyPrecision}
            locale={locale}
          />
          {scanned !== undefined ? (
            uoms.length > 1 ? (
              <label className="op-field-label">
                {t("production.floor.op.uom")}
                <select
                  className="floor-select"
                  value={effectiveUom ?? ""}
                  data-testid="op-material-uom"
                  onChange={(event) => {
                    setUom(event.target.value);
                  }}
                >
                  {uoms.map((code) => (
                    <option key={code} value={code}>
                      {unitSymbol(code, { locale })}
                    </option>
                  ))}
                </select>
              </label>
            ) : effectiveUom === undefined ? (
              // No expected line to borrow a UoM from: the scan path cannot
              // compose a legal payload — stated, not guessed.
              <p className="floor-hint floor-hint-warn" data-testid="op-material-no-uom">
                {t("production.floor.op.noUom")}
              </p>
            ) : null
          ) : null}
          <FloorButton
            onClick={issue}
            variant="primary"
            disabled={
              disabled ||
              !(Number(normalizeQty(qty)) > 0) ||
              (scanned !== undefined && effectiveUom === undefined)
            }
            disabledReason={
              disabled
                ? gate.reason
                : !(Number(normalizeQty(qty)) > 0)
                  ? t("production.floor.op.materialNeedsQty")
                  : t("production.floor.op.noUom")
            }
            testId="op-material-issue"
          >
            {mode === "return"
              ? t("production.floor.op.returnButton")
              : t("production.floor.op.issueButton")}
          </FloorButton>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Calibration form (S10, 04-C-2 — spec 04 §3.3 step 7, §5.1)
// ---------------------------------------------------------------------------

const CALIBRATION_METHODS: readonly CalibrationMethod[] = [
  "scale_weight",
  "meter_counter",
  "manual_tape",
];

/**
 * The S10 calibration form on a `requires_measured_capture` step: "Machine
 * says" (the vendor-locked display's CLAIM — copied by the worker, §11's
 * stated manual double-entry) and "Measured" (scale/meter TRUTH — the only
 * number costing and inventory ever use, D-025), plus the barrel label, the
 * method toggle and the optional barrel scan. Enqueues `calibration_recorded`
 * through the ONE outbox like every other tap; validation mirrors §2.8's
 * positivity CHECKs client-side, never the server's whole guard.
 */
function CalibrationPanel({
  gate,
  onRecord,
}: {
  gate: TapGate;
  onRecord: (input: {
    batchLabel: string;
    machineClaimedQty: string;
    measuredQty: string;
    uom: string;
    method: CalibrationMethod;
    outputScanCode?: string | undefined;
  }) => void;
}) {
  const { t } = useTranslation();
  const disabled = gate.blocked;
  const [batchLabel, setBatchLabel] = useState("");
  const [claimed, setClaimed] = useState("");
  const [measured, setMeasured] = useState("");
  const [uom, setUom] = useState("");
  const [method, setMethod] = useState<CalibrationMethod>("meter_counter");
  const [scanCode, setScanCode] = useState("");
  const [problem, setProblem] = useState(false);

  const record = (): void => {
    const claimedText = normalizeQty(claimed);
    const measuredText = normalizeQty(measured);
    if (
      batchLabel.trim().length === 0 ||
      uom.trim().length === 0 ||
      !(Number(claimedText) > 0) ||
      !(Number(measuredText) > 0)
    ) {
      setProblem(true);
      return;
    }
    setProblem(false);
    onRecord({
      batchLabel: batchLabel.trim(),
      machineClaimedQty: claimedText,
      measuredQty: measuredText,
      uom: uom.trim(),
      method,
      ...(scanCode.trim().length > 0 ? { outputScanCode: scanCode.trim() } : {}),
    });
    setBatchLabel("");
    setClaimed("");
    setMeasured("");
    setScanCode("");
  };

  return (
    <section className="floor-panel" data-testid="op-calibration-panel">
      <h2 className="floor-panel-head">{t("production.floor.op.calibration")}</h2>

      <span className="op-field-label">{t("production.floor.op.calibrationBatch")}</span>
      <input
        className="op-qty-input op-cal-input"
        value={batchLabel}
        onChange={(e) => {
          setBatchLabel(e.target.value);
        }}
        placeholder={t("production.floor.op.calibrationBatchPlaceholder")}
        data-testid="op-cal-batch"
      />

      <span className="op-field-label">{t("production.floor.op.calibrationClaimed")}</span>
      <input
        className="op-qty-input op-cal-input"
        inputMode="decimal"
        value={claimed}
        onChange={(e) => {
          setClaimed(e.target.value);
        }}
        data-testid="op-cal-claimed"
      />

      <span className="op-field-label">{t("production.floor.op.calibrationMeasured")}</span>
      <input
        className="op-qty-input op-cal-input"
        inputMode="decimal"
        value={measured}
        onChange={(e) => {
          setMeasured(e.target.value);
        }}
        data-testid="op-cal-measured"
      />

      <span className="op-field-label">{t("production.floor.op.uom")}</span>
      <input
        className="op-qty-input op-cal-input"
        value={uom}
        onChange={(e) => {
          setUom(e.target.value);
        }}
        placeholder={t("production.floor.op.calibrationUomPlaceholder")}
        data-testid="op-cal-uom"
      />

      <span className="op-field-label">{t("production.floor.op.calibrationMethod")}</span>
      <div className="reason-grid" data-testid="op-cal-method">
        {CALIBRATION_METHODS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            className="floor-btn"
            aria-pressed={method === candidate}
            data-testid={`op-cal-method-${candidate}`}
            onClick={() => {
              setMethod(candidate);
            }}
          >
            {t(`production.floor.op.calibrationMethods.${candidate}`)}
          </button>
        ))}
      </div>

      <span className="op-field-label">{t("production.floor.op.calibrationScan")}</span>
      <input
        className="op-qty-input op-cal-input"
        value={scanCode}
        onChange={(e) => {
          setScanCode(e.target.value);
        }}
        placeholder={t("production.floor.op.calibrationScanPlaceholder")}
        data-testid="op-cal-scan"
      />

      {problem ? (
        <p className="floor-hint floor-hint-warn" role="alert" data-testid="op-cal-problem">
          {t("production.floor.op.calibrationProblem")}
        </p>
      ) : null}

      <FloorButton
        onClick={record}
        variant="primary"
        disabled={disabled}
        disabledReason={gate.reason}
        testId="op-cal-record"
      >
        {t("production.floor.op.calibrationRecord")}
      </FloorButton>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Serial/lot capture (S10, 04-C-3 — spec 04 §3.3 step 8, §5.1, §2.14)
// ---------------------------------------------------------------------------

/**
 * The S10 serial capture (§4 row 10's "serial capture when required").
 *
 * THE FIVE UX DECISIONS THIS PANEL MAKES, and why:
 *
 * 1. THE SCAN IS THE PRIMARY PATH. The field is `ScanInput` with
 *    `captureWedge`, so a ring scanner's burst lands here even when the
 *    operator's thumb is in the quantity box — that exact mis-target is how a
 *    barcode ends up in a length field and the tap dies on validation. (That
 *    claim was aspirational until 2026-08-06: `ScanInput` bailed whenever any
 *    editable field had focus, so the mis-target happened exactly as described.
 *    It is enforced and tested in the component now — D-086.) The
 *    engraved serial is a SECOND field beside it, always visible, never a mode
 *    to find: §9.15's damaged-label path is the normal case for a cable that
 *    was lasered before anyone printed a label.
 * 2. THE DIRECTION IS TWO LABELLED BUTTONS, not a toggle or an arrow. A worker
 *    picks "Came off this machine" or "Went into this machine" — the physical
 *    sentence, in the tense they would say it. `aria-pressed` carries the state
 *    for a screen reader and the selected button is visually distinct, so the
 *    answer is never inferred from position.
 * 3. THE UNIT IS PREFILLED from the work order's own UoM (the §5 e14/e15 card's
 *    `uom`, added with this slice) rather than typed per barrel. It stays
 *    editable, because a bobbin measured in kilograms on a metre order is a
 *    real thing, but the default is the one the server already knows.
 * 4. A CAPTURE ON A NON-REQUIRING STEP IS OFFERED, collapsed behind one button.
 *    Serials get engraved on steps nobody flagged, and refusing to record them
 *    throws away traceability nobody can reconstruct later. On a REQUIRING step
 *    the form is open from the start — it is the thing standing between the
 *    operator and Complete.
 * 5. THE REFUSAL IS ACTIONABLE. An unresolvable scan surfaces through the
 *    screen's `TapResult` naming THE CODE and the next move, never the server's
 *    `unknown_reference` enum.
 */
function SerialCapturePanel({
  required,
  gate,
  defaultUom,
  defaultPrecision,
  locale,
  onCapture,
}: {
  required: boolean;
  gate: TapGate;
  defaultUom: string;
  /** The entry precision of `defaultUom` (D-091). The UoM field stays
   * editable (a bobbin weighed in kg on a metre order is real), and the
   * precision follows the ORDER's unit — the server, not this form, is the
   * authority on the edited unit's scale; the wire's 3-dp ceiling still
   * binds either way. */
  defaultPrecision: number | undefined;
  locale: string;
  onCapture: (input: {
    direction: SerialLotDirection;
    serialNo?: string | undefined;
    scanCode?: string | undefined;
    qty: string;
    uom: string;
  }) => void;
}) {
  const { t } = useTranslation();
  const disabled = gate.blocked;
  const [open, setOpen] = useState(required);
  const [direction, setDirection] = useState<SerialLotDirection>("produced");
  const [serialNo, setSerialNo] = useState("");
  const [scanCode, setScanCode] = useState("");
  const [qty, setQty] = useState("0");
  const [uom, setUom] = useState(defaultUom);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const capture = (): void => {
    const value = normalizeQty(qty);
    const invalid = serialCaptureProblem({ serialNo, scanCode, qty: value, uom });
    if (invalid !== undefined) {
      setProblem(invalid);
      return;
    }
    setProblem(undefined);
    onCapture({
      direction,
      ...(serialNo.trim().length > 0 ? { serialNo: serialNo.trim() } : {}),
      ...(scanCode.trim().length > 0 ? { scanCode: scanCode.trim() } : {}),
      qty: value,
      uom: uom.trim(),
    });
    setSerialNo("");
    setScanCode("");
    setQty("0");
  };

  return (
    <section className="floor-panel" data-testid="op-serial-panel">
      <h2 className="floor-panel-head">{t("production.floor.op.serial")}</h2>

      {!open ? (
        <FloorButton
          onClick={() => {
            setOpen(true);
          }}
          disabled={disabled}
          disabledReason={gate.reason}
          testId="op-serial-open"
        >
          {t("production.floor.op.serialOpen")}
        </FloorButton>
      ) : (
        <>
          <span className="op-field-label">{t("production.floor.op.serialDirection")}</span>
          <div className="reason-grid" data-testid="op-serial-direction">
            {(["produced", "consumed"] as const).map((candidate) => (
              <button
                key={candidate}
                type="button"
                className="floor-btn"
                aria-pressed={direction === candidate}
                data-testid={`op-serial-direction-${candidate}`}
                onClick={() => {
                  setDirection(candidate);
                }}
              >
                {t(`production.floor.op.serialDirections.${candidate}`)}
              </button>
            ))}
          </div>
          {/* The direction restated as a sentence, so the chosen button's
              MEANING is on screen and not only its label. */}
          <p className="floor-hint" data-testid="op-serial-direction-note">
            {t(`production.floor.op.serialDirectionNote.${direction}`)}
          </p>

          <ScanInput
            autoFocus={false}
            captureWedge
            onScan={(payload) => {
              setScanCode(payload.trim());
            }}
            label={t("production.floor.op.serialScanLabel")}
            placeholder={t("production.floor.op.serialScanPlaceholder")}
          />
          {scanCode.length > 0 ? (
            <p className="floor-hint" data-testid="op-serial-scanned">
              {t("production.floor.op.serialScanned", { code: scanCode })}
            </p>
          ) : null}

          <span className="op-field-label">{t("production.floor.op.serialNo")}</span>
          <input
            className="op-qty-input op-cal-input"
            value={serialNo}
            autoCapitalize="characters"
            autoComplete="off"
            onChange={(e) => {
              setSerialNo(e.target.value);
            }}
            placeholder={t("production.floor.op.serialNoPlaceholder")}
            data-testid="op-serial-no"
          />

          <span className="op-field-label">{t("production.floor.op.serialQty")}</span>
          <QtyStepper
            value={qty}
            onChange={setQty}
            testId="op-serial-qty"
            precision={defaultPrecision}
            locale={locale}
          />

          <span className="op-field-label">{t("production.floor.op.uom")}</span>
          <input
            className="op-qty-input op-cal-input"
            value={uom}
            onChange={(e) => {
              setUom(e.target.value);
            }}
            data-testid="op-serial-uom"
          />

          {problem !== undefined ? (
            <p className="floor-hint floor-hint-warn" role="alert" data-testid="op-serial-problem">
              {t(`production.floor.op.serialProblem.${problem}`)}
            </p>
          ) : null}

          <FloorButton
            onClick={capture}
            variant="primary"
            disabled={disabled}
            disabledReason={gate.reason}
            testId="op-serial-capture"
          >
            {t("production.floor.op.serialCapture")}
          </FloorButton>
        </>
      )}
    </section>
  );
}
